import { useEffect, useCallback, useState, useRef } from "react";
import type { TokenAction, TokenActionCategory } from "@roll1/shared";
import { useI18n } from "../i18n";
import {
  Sword, Wand2, Shield, Zap, CircleDot,
} from "lucide-react";

interface TokenActionHUDProps {
  actions: TokenAction[];
  onAction: (action: TokenAction) => void;
  tokenScreenPos: { x: number; y: number };
  tokenSize: number;
  viewportWidth: number;
  viewportHeight: number;
}

const CATEGORY_ORDER: TokenActionCategory[] = ['attack', 'spell', 'ability', 'other'];

const CATEGORY_ICONS: Record<TokenActionCategory, React.ReactNode> = {
  attack: <Sword className="w-3.5 h-3.5" />,
  spell: <Wand2 className="w-3.5 h-3.5" />,
  ability: <Shield className="w-3.5 h-3.5" />,
  other: <CircleDot className="w-3.5 h-3.5" />,
};

function getActionIcon(action: TokenAction): React.ReactNode {
  if (action.icon === 'sword') return <Sword className="w-4 h-4" />;
  if (action.icon === 'wand') return <Wand2 className="w-4 h-4" />;
  if (action.icon === 'shield') return <Shield className="w-4 h-4" />;
  if (action.icon === 'zap') return <Zap className="w-4 h-4" />;
  // Default icon based on category
  const cat = action.category ?? 'other';
  return CATEGORY_ICONS[cat];
}

function groupByCategory(actions: TokenAction[]): Map<TokenActionCategory, TokenAction[]> {
  const grouped = new Map<TokenActionCategory, TokenAction[]>();
  for (const cat of CATEGORY_ORDER) {
    grouped.set(cat, []);
  }
  for (const action of actions) {
    const cat = action.category ?? 'other';
    const list = grouped.get(cat) ?? [];
    list.push(action);
    grouped.set(cat, list);
  }
  // Remove empty categories
  for (const cat of CATEGORY_ORDER) {
    if (grouped.get(cat)!.length === 0) {
      grouped.delete(cat);
    }
  }
  return grouped;
}

export default function TokenActionHUD({
  actions,
  onAction,
  tokenScreenPos,
  tokenSize,
  viewportWidth,
  viewportHeight,
}: TokenActionHUDProps) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const hudRef = useRef<HTMLDivElement>(null);

  // Sort actions by category order, then sortOrder
  const sorted = [...actions].sort((a, b) => {
    const catA = CATEGORY_ORDER.indexOf(a.category ?? 'other');
    const catB = CATEGORY_ORDER.indexOf(b.category ?? 'other');
    if (catA !== catB) return catA - catB;
    return a.sortOrder - b.sortOrder;
  });

  // Keyboard shortcuts 1-9
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Guard: not in input/textarea
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (actions.length === 0) return;

    const num = parseInt(e.key, 10);
    if (num >= 1 && num <= 9 && num <= sorted.length) {
      e.preventDefault();
      onAction(sorted[num - 1]);
    }
  }, [actions, sorted, onAction]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (actions.length === 0) return null;

  // Smart positioning: prefer right of token, fallback to left
  const HUD_WIDTH = expanded ? 220 : 160;
  const HUD_MAX_HEIGHT = 300;
  const OFFSET = 8;

  let left = tokenScreenPos.x + tokenSize + OFFSET;
  let top = tokenScreenPos.y;

  // Avoid right edge
  if (left + HUD_WIDTH > viewportWidth - 10) {
    left = tokenScreenPos.x - HUD_WIDTH - OFFSET;
  }
  // Avoid bottom edge
  if (top + HUD_MAX_HEIGHT > viewportHeight - 10) {
    top = viewportHeight - HUD_MAX_HEIGHT - 10;
  }
  // Avoid top edge
  if (top < 10) top = 10;

  const grouped = groupByCategory(sorted);
  let actionIndex = 0;

  return (
    <div
      ref={hudRef}
      className="fixed z-40 bg-popover border border-border rounded-lg shadow-lg p-2 transition-opacity duration-150"
      style={{ left, top, width: HUD_WIDTH, maxHeight: HUD_MAX_HEIGHT, overflow: 'auto' }}
    >
      {/* Mode toggle */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="text-xs text-muted-foreground mb-1.5 hover:text-foreground transition-colors"
      >
        {expanded ? t('actionHud.compact') : t('actionHud.expanded')}
      </button>

      {Array.from(grouped.entries()).map(([category, categoryActions]) => (
        <div key={category} className="mb-1.5">
          {/* Section header */}
          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-0.5 px-1">
            {CATEGORY_ICONS[category]}
            <span>{t(`actionHud.${category}` as 'actionHud.attack')}</span>
          </div>

          {/* Actions */}
          <div className={expanded ? "space-y-0.5" : "flex flex-wrap gap-1"}>
            {categoryActions.map((action) => {
              actionIndex++;
              const shortcutNum = actionIndex <= 9 ? actionIndex : null;

              return (
                <button
                  key={action.id}
                  type="button"
                  className={`relative flex items-center gap-1.5 rounded px-2 py-1 text-sm hover:bg-accent transition-colors ${
                    expanded ? 'w-full' : ''
                  }`}
                  style={action.color ? { borderLeft: `3px solid ${action.color}` } : undefined}
                  title={`${action.name}: ${action.rollExpression}`}
                  onClick={() => onAction(action)}
                  aria-label={`${action.name} (${action.rollExpression})`}
                >
                  {getActionIcon(action)}
                  {expanded && (
                    <div className="flex-1 min-w-0 text-left">
                      <div className="truncate text-foreground">{action.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{action.rollExpression}</div>
                    </div>
                  )}
                  {!expanded && (
                    <span className="truncate text-xs">{action.name}</span>
                  )}
                  {shortcutNum && (
                    <span className="absolute top-0 right-0 text-[10px] text-muted-foreground bg-secondary rounded-bl px-0.5">
                      {shortcutNum}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
